import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BookOpen, BookMarked, Clock, Award, ArrowRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/layout/Navbar';
import Header from '@/components/layout/Header';
import CourseCard, { CourseData } from '@/components/ui/course-card/CourseCard';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from '@/components/ui/use-toast';
import { fetchCourses } from '@/services/courses';
import { fetchLessons, fetchLessonsByCourseId } from '@/services/lessons';
import { getProgressOverview, getProgressStats } from '@/services/progress';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';

const Dashboard: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState<CourseData[]>([]);
  const [inProgressCourses, setInProgressCourses] = useState<CourseData[]>([]);
  const [completedCourses, setCompletedCourses] = useState<CourseData[]>([]);
  const [featuredLessons, setFeaturedLessons] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCourses: 0,
    totalLessons: 0,
    lessonsCompleted: 0,
    avgProgress: 0
  });

  useAuthRedirect(false);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchCoursesData();
    } else if (!isAuthenticated) {
      setIsLoading(false);
    }
  }, [isAuthenticated, navigate, user]);

  const fetchCoursesData = async () => {
    setIsLoading(true);
    try {
      // Get all courses
      const coursesResponse = await fetchCourses();
      
      // Log response for debugging
      console.log('fetchCourses response:', coursesResponse);
      
      // Handle different response formats
      let coursesData = [];
      
      if (coursesResponse) {
        if (Array.isArray(coursesResponse)) {
          coursesData = coursesResponse;
        } else if (coursesResponse.courses && Array.isArray(coursesResponse.courses)) {
          coursesData = coursesResponse.courses;
        } else if (typeof coursesResponse === 'object') {
          coursesData = [coursesResponse];
        }
      }
      
      console.log('Processed courses data:', coursesData);
      
      if (!coursesData.length) {
        console.log('No courses found');
        setCourses([]);
        setInProgressCourses([]);
        setCompletedCourses([]);
        setIsLoading(false);
        return;
      }
      
      // Get user progress data from API
      const progressOverview = await getProgressOverview();
      const progressStats = await getProgressStats();
      
      console.log('Progress overview:', progressOverview);
      console.log('Progress stats:', progressStats);
      
      // Create a map of course progress
      const progressMap = new Map();
      if (progressOverview && progressOverview.courseProgress) {
        progressOverview.courseProgress.forEach(item => {
          progressMap.set(item.courseId, {
            progress: item.progress || 0,
            completedLessons: item.completedLessons || 0,
            totalLessons: item.totalLessons || 0
          });
        });
      }
      
      const coursesWithLessons = await Promise.all(
        coursesData.map(async (course) => {
          try {
            const lessons = await fetchLessonsByCourseId(course.id);
            
            // Get progress for this course
            const progress = progressMap.get(course.id) || { 
              progress: 0, 
              completedLessons: 0,
              totalLessons: lessons ? lessons.length : 0
            };
            
            // Calculate if course is completed
            const isCompleted = progress.completedLessons > 0 && 
                               progress.completedLessons === progress.totalLessons;
            
            return {
              id: course.id,
              title: course.title,
              description: course.description || '',
              coverImage: course.imageUrl || course.coverImage || "/thumbnail.webp",
              totalLessons: lessons ? lessons.length : 0,
              estimatedTime: course.estimatedTime || `${Math.max(1, Math.round((lessons ? lessons.length : 0) / 2))} hours`,
              progress: progress.progress,
              completed: isCompleted,
              status: course.status === 'published' ? 'published' as const : 
                     (course.isPublished ? 'published' as const : 'draft' as const),
              lessons: lessons
            };
          } catch (err) {
            console.error(`Error processing course ${course.id}:`, err);
            return null;
          }
        })
      );
      
      // Filter out any null entries from errors
      const validCourses = coursesWithLessons.filter(Boolean);
      
      const availableCourses = validCourses.filter(course => 
        course.status === 'published' || (user?.role === 'admin' && course.status === 'draft')
      );
      
      // Update course status based on progress
      const coursesWithProgress = availableCourses;
      
      setCourses(coursesWithProgress);
      
      // Set in-progress and completed courses
      setInProgressCourses(coursesWithProgress.filter(c => c.progress > 0 && !c.completed));
      setCompletedCourses(coursesWithProgress.filter(c => c.completed));
      
      // Set statistics
      setStats({
        totalCourses: progressStats.totalCourses || coursesWithProgress.length,
        totalLessons: progressStats.totalLessons || coursesWithProgress.reduce((sum, course) => sum + course.totalLessons, 0),
        lessonsCompleted: progressStats.completedLessons || 0,
        avgProgress: progressOverview.totalProgress || 0
      });
      
      // Create featured lessons
      const featuredLessonsData = [];
      
      for (const course of availableCourses) {
        if (course.lessons && course.lessons.length > 0) {
          const firstLesson = course.lessons[0];
          featuredLessonsData.push({
            id: firstLesson.id,
            title: firstLesson.title,
            description: `${course.title} - Lesson ${firstLesson.orderIndex + 1}`,
            courseId: course.id,
            courseName: course.title,
            duration: course.estimatedTime || '30 min',
            imageUrl: course.coverImage,
            status: course.status
          });
          
          if (featuredLessonsData.length >= 3) break;
        }
      }
      
      setFeaturedLessons(featuredLessonsData);
      
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast({
        title: "Failed to load courses",
        description: "There was an error loading your courses.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <p>Loading your courses...</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      
      <main className="flex-1 container py-6">
        <Header 
          title={`Welcome, ${user?.fullName || 'User'}`} 
          description="Continue your learning journey" 
        />
        
        <div className="mb-8 grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Courses</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <BookOpen className="mr-2 h-4 w-4 text-muted-foreground" />
                <div className="text-2xl font-bold">{stats.totalCourses}</div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Lessons Completed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <BookMarked className="mr-2 h-4 w-4 text-muted-foreground" />
                <div className="text-2xl font-bold">
                  {stats.lessonsCompleted} / {stats.totalLessons}
                </div>
              </div>
              <Progress 
                value={stats.totalLessons > 0 ? (stats.lessonsCompleted / stats.totalLessons) * 100 : 0} 
                className="h-1 mt-2" 
              />
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Average Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Clock className="mr-2 h-4 w-4 text-muted-foreground" />
                <div className="text-2xl font-bold">{stats.avgProgress}%</div>
              </div>
              <Progress value={stats.avgProgress} className="h-1 mt-2" />
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Courses Completed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Award className="mr-2 h-4 w-4 text-muted-foreground" />
                <div className="text-2xl font-bold">{completedCourses.length}</div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <p>Loading your courses...</p>
          </div>
        ) : (
          <>
            {/* In Progress Section */}
            {inProgressCourses.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold">Continue Learning</h2>
                  <Button variant="ghost" asChild size="sm">
                    <Link to="/courses" className="flex items-center text-sm">
                      View all courses <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
                
                <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                  {inProgressCourses.map(course => (
                    <CourseCard key={course.id} course={course} compact={true} />
                  ))}
                </div>
              </div>
            )}
            
            {/* Completed Courses Section */}
            {completedCourses.length > 0 ? (
              <div className="mb-8">
                <h2 className="text-xl font-semibold mb-4">Completed Courses</h2>
                <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                  {completedCourses.map(course => (
                    <CourseCard key={course.id} course={course} compact={true} />
                  ))}
                </div>
              </div>
            ) : courses.length === 0 ? (
              <div className="rounded-lg border border-dashed p-8 text-center">
                <BookOpen className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-lg font-medium">No courses available</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Check back later for new courses.
                </p>
              </div>
            ) : null}
            
            {/* All Courses Section (only show if we have courses but none completed or in progress) */}
            {courses.length > 0 && inProgressCourses.length === 0 && completedCourses.length === 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold mb-4">Available Courses</h2>
                <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                  {courses.map(course => (
                    <CourseCard key={course.id} course={course} compact={true} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
